import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Loader2, Send } from "lucide-react";
import { FadeIn, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-brand-red/60";

const INFO = [
  { icon: MapPin, label: "Head Office", value: "Kainakari Building, 2nd Floor (above ESAF Bank), Near Powerhouse, Alappuzha, Kerala 688007, India" },
  { icon: Phone, label: "Phone", value: "+91 79074 40408  •  +91 96050 96999" },
  { icon: Mail, label: "Email", value: "techworldgroup22@gmail.com" },
  { icon: Globe, label: "Website", value: "www.techworldgroup.co.in" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Enquiry sent. We'll get back to you within one working day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send right now. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative bg-surface py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="06 — Contact"
          title="Verify us. Then talk to us."
          description="Call, write, or drop by the head office — candidates and partners are always welcome."
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="flex flex-col gap-8 lg:col-span-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {INFO.map((item, i) => (
                <FadeIn key={item.label} delay={i * 0.06} className="h-full">
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors duration-300 hover:border-brand-red/40">
                    <item.icon size={20} className="text-brand-red" />
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {item.label}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-200">{item.value}</p>
                  </div>
                </FadeIn>
              ))}
            </div>

            <FadeIn delay={0.2}>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <iframe
                  title="Techworld Business Group head office location — Alappuzha, Kerala"
                  src="https://www.google.com/maps?q=TECHWORLD+GROUP,+Alappuzha,+Kerala&output=embed"
                  className="h-64 w-full border-0 grayscale-[35%] invert-[90%] hue-rotate-180 sm:h-72"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  href="https://share.google/yncllLArmGAzWVLwX"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-maps-link"
                  className="flex items-center justify-center gap-2 border-t border-white/10 bg-ink/60 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-brand-bright"
                >
                  <MapPin size={13} className="text-brand-red" />
                  Open our Google Maps listing
                </a>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.12} className="lg:col-span-7">
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8">
              <h3 className="font-display text-2xl font-semibold text-white">Send an enquiry</h3>
              <p className="mt-2 text-sm text-slate-400">
                Business, career or documentation — one form for everything.
              </p>
              <form data-testid="contact-enquiry-form" onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="enquiry-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="enquiry-email-input" className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
                <input data-testid="enquiry-phone-input" className={inputCls} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input data-testid="enquiry-subject-input" className={inputCls} placeholder="Subject" value={form.subject} onChange={set("subject")} />
                <textarea
                  data-testid="enquiry-message-input"
                  className={`${inputCls} min-h-32 resize-y sm:col-span-2`}
                  placeholder="Your message *"
                  value={form.message}
                  onChange={set("message")}
                />
                <button
                  data-testid="enquiry-submit-button"
                  type="submit"
                  disabled={sending}
                  className="flex items-center justify-center gap-2 rounded-full bg-brand-red px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-brand-bright hover:shadow-[0_0_28px_rgba(220,38,38,0.45)] disabled:opacity-60 sm:col-span-2 sm:w-fit"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Contact;
